import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContactButton from "../components/FloatingContactButton";
import { Link, useLocation } from "react-router-dom";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import SEO from "../components/SEO";

const Home = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState({
    hero: false,
    services: false,
    about: false,
    testimonials: false,
    stats: false,
    process: false,
    gallery: false,
    cta: false,
  });

  // Animation when scrolling
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById("hero-section");
      const servicesSection = document.getElementById("services-section");
      const aboutSection = document.getElementById("about-section");
      const testimonialsSection = document.getElementById(
        "testimonials-section"
      );
      const statsSection = document.getElementById("stats-section");
      const processSection = document.getElementById("process-section");
      const gallerySection = document.getElementById("gallery-section");
      const ctaSection = document.getElementById("cta-section");

      if (heroSection) {
        setIsVisible((prev) => ({ ...prev, hero: true })); // Always visible
      }

      if (
        servicesSection &&
        window.scrollY > servicesSection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, services: true }));
      }

      if (
        aboutSection &&
        window.scrollY > aboutSection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, about: true }));
      }

      if (
        testimonialsSection &&
        window.scrollY >
          testimonialsSection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, testimonials: true }));
      }

      if (
        statsSection &&
        window.scrollY > statsSection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, stats: true }));
      }

      if (
        processSection &&
        window.scrollY > processSection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, process: true }));
      }

      if (
        gallerySection &&
        window.scrollY > gallerySection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, gallery: true }));
      }

      if (
        ctaSection &&
        window.scrollY > ctaSection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, cta: true }));
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Call once to set initial visibility
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle hash links for smooth scrolling
  useEffect(() => {
    // Check if there's a hash in the URL
    if (location.hash) {
      // Get the element by id (without the # character)
      const element = document.getElementById(location.hash.substring(1));

      if (element) {
        // Wait a bit for page to fully load
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Bearz Landscaping | Best Landscaping Services in Montreal West Island"
        description="Premium landscaping services in Montreal's West Island. Professional lawn care, garden maintenance, hedge trimming and property services with over a decade of experience."
        pathname="/"
      />
      <Navbar />

      {/* Hero Section */}
      <section
        id="hero-section"
        className={`relative min-h-[100vh] flex items-center justify-center overflow-hidden transition-opacity duration-1000 ease-in-out ${
          isVisible.hero ? "opacity-100" : "opacity-0"
        }`}
        aria-label="Premium landscaping services in Montreal"
      >
        <div className="absolute inset-0 bg-primary-900/60 z-10"></div>
        <div className="absolute inset-0">
          <img
            src="/hero.png"
            alt="Professional landscaping services in Montreal West Island - Bearz Landscaping"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 z-20 text-center">
          <h1 className="text-3xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 animate-fadeInUp">
            <span className="block leading-tight">{t("home.hero.title")}</span>
            <span className="block mt-2 sm:mt-2 text-primary-300 text-2xl sm:text-3xl md:text-4xl">
              {t("home.hero.subtitle")}
            </span>
          </h1>
          <p className="text-xl sm:text-lg md:text-xl lg:text-2xl text-white mb-8 sm:mb-10 max-w-3xl mx-auto animate-fadeInUp animation-delay-200">
            <span className="hidden sm:inline">{t("home.hero.serving")}</span>
            <span className="sm:hidden">{t("home.hero.servingMobile")}</span>
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 animate-fadeInUp animation-delay-300">
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full px-6 sm:px-8 py-4 sm:py-4 text-lg sm:text-base md:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              {t("home.hero.getQuote")}
            </Link>
            <a
              href="tel:514-979-8020"
              className="sm:hidden w-full flex items-center justify-center space-x-2 border-2 border-white text-white font-semibold rounded-full px-6 py-4 text-lg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <span>{t("home.hero.callNow")}</span>
            </a>
            <a
              href="#gallery-section"
              className="hidden sm:flex w-full sm:w-auto border-2 border-white hover:border-primary-300 text-white hover:text-primary-300 font-semibold rounded-full px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base md:text-lg transition-all duration-300"
            >
              {t("home.hero.viewProjects")}
            </a>
          </div>
        </div>
        <div className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 z-20 animate-bounce hidden sm:block">
          <a href="#services-section" className="text-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 sm:h-10 sm:w-10"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-label="Scroll down to see our services"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </a>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services-section"
        className={`py-10 sm:py-12 md:py-16 lg:py-20 bg-neutral-50 transition-all duration-1000 ease-in-out ${
          isVisible.services
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 mb-3 sm:mb-4">
              {t("home.services.title")}
            </h2>
            <div className="w-16 sm:w-20 lg:w-24 h-1.5 bg-primary-500 mx-auto rounded-full"></div>
            <p className="mt-3 sm:mt-4 md:mt-6 text-sm sm:text-base md:text-lg lg:text-xl text-neutral-600 max-w-3xl mx-auto">
              {t("home.services.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                title: t("home.services.lawn.title"),
                description: t("home.services.lawn.description"),
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 sm:h-12 sm:w-12 text-primary-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-label="Lawn and Garden Care Service"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                ),
              },
              {
                title: t("home.services.landscape.title"),
                description: t("home.services.landscape.description"),
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 sm:h-12 sm:w-12 text-primary-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-label="Landscape Design Service"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
              },
              {
                title: t("home.services.property.title"),
                description: t("home.services.property.description"),
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 sm:h-12 sm:w-12 text-primary-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-label="Property Maintenance Service"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                ),
              },
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-xl p-5 sm:p-6 md:p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                itemScope
                itemType="https://schema.org/Service"
              >
                <div className="mb-4 sm:mb-6">{service.icon}</div>
                <h3
                  className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-neutral-900"
                  itemProp="name"
                >
                  {service.title}
                </h3>
                <p
                  className="text-sm sm:text-base text-neutral-600 mb-4 sm:mb-6"
                  itemProp="description"
                >
                  {service.description}
                </p>
                <meta
                  itemProp="areaServed"
                  content="Montreal, West Island, Quebec"
                />
                <meta itemProp="provider" content="Bearz Landscaping" />
                <Link
                  to="/services"
                  className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700 transition-colors text-sm sm:text-base"
                >
                  {t("home.services.learnMore")}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 sm:h-5 sm:w-5 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <Link
              to="/services"
              className="inline-block bg-primary-100 hover:bg-primary-200 text-primary-800 font-medium rounded-lg px-4 sm:px-6 py-2 sm:py-3 transition-colors duration-300 text-sm sm:text-base"
            >
              {t("home.services.viewAll")}
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section
        id="stats-section"
        className={`py-12 sm:py-16 bg-primary-800 transition-all duration-1000 ease-in-out ${
          isVisible.stats
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                {isVisible.stats ? (
                  <CountUp
                    start={0}
                    end={2000}
                    duration={2.5}
                    suffix="+"
                    useEasing={true}
                    enableScrollSpy={true}
                    scrollSpyDelay={200}
                  />
                ) : (
                  "2,000+"
                )}
              </div>
              <div className="text-primary-200 text-lg">
                {t("home.stats.clients")}
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                {isVisible.stats ? (
                  <CountUp
                    start={0}
                    end={75000}
                    duration={2.5}
                    separator=","
                    suffix="+"
                    useEasing={true}
                    enableScrollSpy={true}
                    scrollSpyDelay={200}
                  />
                ) : (
                  "75,000+"
                )}
              </div>
              <div className="text-primary-200 text-lg">
                {t("home.stats.lawns")}
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                {isVisible.stats ? (
                  <CountUp
                    start={0}
                    end={12}
                    duration={2.5}
                    suffix="+"
                    useEasing={true}
                    enableScrollSpy={true}
                    scrollSpyDelay={200}
                  />
                ) : (
                  "12+"
                )}
              </div>
              <div className="text-primary-200 text-lg">
                {t("home.stats.years")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Process Section */}
      <section
        id="process-section"
        className={`py-12 sm:py-20 bg-white transition-all duration-1000 ease-in-out ${
          isVisible.process
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              {t("home.process.title")}
            </h2>
            <div className="w-20 sm:w-24 h-1.5 bg-primary-500 mx-auto rounded-full"></div>
            <p className="mt-4 sm:mt-6 text-base sm:text-xl text-neutral-600 max-w-3xl mx-auto">
              {t("home.process.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-primary-100 text-primary-600 text-2xl font-bold mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold mb-4 text-neutral-900">
                {t("home.process.step1.title")}
              </h3>
              <p className="text-neutral-600">
                {t("home.process.step1.description")}
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-primary-100 text-primary-600 text-2xl font-bold mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold mb-4 text-neutral-900">
                {t("home.process.step2.title")}
              </h3>
              <p className="text-neutral-600">
                {t("home.process.step2.description")}
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-primary-100 text-primary-600 text-2xl font-bold mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold mb-4 text-neutral-900">
                {t("home.process.step3.title")}
              </h3>
              <p className="text-neutral-600">
                {t("home.process.step3.description")}
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-block bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg px-8 py-4 transition-colors duration-300 shadow-md"
            >
              {t("home.process.getFreeQuote")}
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about-section"
        className={`py-12 sm:py-20 bg-neutral-50 transition-all duration-1000 ease-in-out ${
          isVisible.about
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="rounded-xl overflow-hidden shadow-lg h-64 sm:h-80 md:h-96 mb-8 md:mb-0">
              <img
                src="/water-front-cut-backyard.JPG"
                alt="Bearz Landscaping waterfront property"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4 sm:mb-6">
                {t("home.about.title")}
              </h2>
              <div className="w-20 sm:w-24 h-1.5 bg-primary-500 rounded-full mb-6 sm:mb-8"></div>
              <p className="text-lg text-neutral-600 mb-6">
                {t("home.about.paragraph1")}
              </p>
              <p className="text-lg text-neutral-600 mb-8">
                {t("home.about.paragraph2")}
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-primary-500 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-neutral-700">
                    {t("home.about.feature1")}
                  </span>
                </div>
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-primary-500 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-neutral-700">
                    {t("home.about.feature2")}
                  </span>
                </div>
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-primary-500 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-neutral-700">
                    {t("home.about.feature3")}
                  </span>
                </div>
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-primary-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-neutral-700">
                    {t("home.about.feature4")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section
        id="testimonials-section"
        className={`py-12 sm:py-20 bg-white transition-all duration-1000 ease-in-out ${
          isVisible.testimonials
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              {t("home.testimonials.title")}
            </h2>
            <div className="w-20 sm:w-24 h-1.5 bg-primary-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                name: "Logan Murphy",
                role: t("home.testimonials.homeowner"),
                quote: t("home.testimonials.quote1"),
              },
              {
                name: "Max Pilch",
                role: t("home.testimonials.homeowner"),
                quote: t("home.testimonials.quote2"),
              },
              {
                name: "Max de Belle",
                role: t("home.testimonials.homeowner"),
                quote: t("home.testimonials.quote3"),
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-6 sm:p-8 relative flex flex-col h-full"
              >
                <svg
                  className="h-10 w-10 sm:h-12 sm:w-12 text-primary-100 absolute top-4 sm:top-6 left-4 sm:left-6"
                  fill="currentColor"
                  viewBox="0 0 32 32"
                  aria-hidden="true"
                >
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <p className="text-neutral-600 relative z-10 mt-8 sm:mt-4 flex-grow">
                  {testimonial.quote}
                </p>
                <div className="w-full h-px bg-neutral-200 my-4"></div>
                <div className="flex items-center mt-auto">
                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold">
                    {testimonial.name[0]}
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-neutral-900">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-neutral-500">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        id="cta-section"
        className={`relative py-12 sm:py-20 bg-primary-800 transition-all duration-1000 ease-in-out ${
          isVisible.cta
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div className="absolute inset-0 bg-primary-800 opacity-75"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            {t("home.cta.title")}
          </h2>
          <p className="text-base sm:text-xl text-primary-100 mb-6 sm:mb-10 max-w-3xl mx-auto">
            {t("home.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-white hover:bg-neutral-100 text-primary-800 font-semibold rounded-full px-8 py-3 sm:py-4 text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              {t("home.cta.getQuote")}
            </Link>
            <a
              href="tel:514-979-8020"
              className="flex items-center text-white hover:text-primary-200 transition-colors duration-300 mt-4 sm:mt-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              514-979-8020
            </a>
          </div>
        </div>
      </section>

      {/* New Gallery Section */}
      <section
        id="gallery-section"
        className={`py-12 sm:py-20 bg-white transition-all duration-1000 ease-in-out ${
          isVisible.gallery
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
        itemScope
        itemType="https://schema.org/ImageGallery"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4"
              itemProp="name"
            >
              {t("home.gallery.title")}
            </h2>
            <div className="w-20 sm:w-24 h-1.5 bg-primary-500 mx-auto rounded-full"></div>
            <p
              className="mt-4 sm:mt-6 text-base sm:text-xl text-neutral-600 max-w-3xl mx-auto"
              itemProp="description"
            >
              {t("home.gallery.subtitle")}
            </p>
            <meta
              itemProp="about"
              content="Landscaping projects in Montreal West Island"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Project 1 - Before and After Fall Cleanup */}
            <div
              className="overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <div className="relative h-72">
                <img
                  src="/fall_leaves_before.jpeg"
                  alt="Fall leaf cleanup before - Montreal West Island by Bearz Landscaping"
                  className="absolute w-full h-full object-cover transition-opacity duration-500 ease-in-out"
                  itemProp="contentUrl"
                />
                <img
                  src="/fall_leaves_after_cleanedup.jpeg"
                  alt="Fall leaf cleanup after - Montreal West Island by Bearz Landscaping"
                  className="absolute w-full h-full object-cover opacity-0 hover:opacity-100 transition-opacity duration-500 ease-in-out"
                  itemProp="contentUrl"
                />
                <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs px-2 py-1 rounded-full">
                  {t("home.gallery.hoverToSeeAfter")}
                </div>
              </div>
              <div className="p-4 bg-white">
                <h3
                  className="font-semibold text-lg text-neutral-900"
                  itemProp="name"
                >
                  {t("home.gallery.fallCleanup.title")}
                </h3>
                <p className="text-neutral-600 mt-1" itemProp="description">
                  {t("home.gallery.fallCleanup.description")}
                </p>
              </div>
            </div>

            {/* Project 2 - Grass Replacement Before/After */}
            <div
              className="overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <div className="relative h-72">
                <img
                  src="/grass_replace_before1.jpeg"
                  alt="Lawn replacement before - Montreal West Island"
                  className="absolute w-full h-full object-cover transition-opacity duration-500 ease-in-out"
                  itemProp="contentUrl"
                />
                <img
                  src="/grass_replace_after1.jpeg"
                  alt="Lawn replacement after - Montreal West Island"
                  className="absolute w-full h-full object-cover opacity-0 hover:opacity-100 transition-opacity duration-500 ease-in-out"
                  itemProp="contentUrl"
                />
                <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs px-2 py-1 rounded-full">
                  {t("home.gallery.hoverToSeeAfter")}
                </div>
              </div>
              <div className="p-4 bg-white">
                <h3
                  className="font-semibold text-lg text-neutral-900"
                  itemProp="name"
                >
                  {t("home.gallery.lawnRejuvenation.title")}
                </h3>
                <p className="text-neutral-600 mt-1" itemProp="description">
                  {t("home.gallery.lawnRejuvenation.description")}
                </p>
              </div>
            </div>

            {/* Project 3 - Trimmed Bush */}
            <div
              className="overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <img
                src="/trimmed-shaped-bush.jpg"
                alt="Professional bush trimming services in Montreal West Island by Bearz Landscaping"
                className="w-full h-72 object-cover"
                itemProp="contentUrl"
              />
              <div className="p-4 bg-white">
                <h3
                  className="font-semibold text-lg text-neutral-900"
                  itemProp="name"
                >
                  {t("home.gallery.project1.title")}
                </h3>
                <p className="text-neutral-600 mt-1" itemProp="description">
                  {t("home.gallery.project1.description")}
                </p>
              </div>
            </div>

            {/* Project 4 - Another Grass Replacement */}
            <div
              className="overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <div className="relative h-72">
                <img
                  src="/grass_replace_before2.jpeg"
                  alt="Lawn restoration before - Montreal West Island"
                  className="absolute w-full h-full object-cover transition-opacity duration-500 ease-in-out"
                  itemProp="contentUrl"
                />
                <img
                  src="/grass_replace_after2.jpeg"
                  alt="Lawn restoration after - Montreal West Island"
                  className="absolute w-full h-full object-cover opacity-0 hover:opacity-100 transition-opacity duration-500 ease-in-out"
                  itemProp="contentUrl"
                />
                <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs px-2 py-1 rounded-full">
                  {t("home.gallery.hoverToSeeAfter")}
                </div>
              </div>
              <div className="p-4 bg-white">
                <h3
                  className="font-semibold text-lg text-neutral-900"
                  itemProp="name"
                >
                  {t("home.gallery.lawnRestoration.title")}
                </h3>
                <p className="text-neutral-600 mt-1" itemProp="description">
                  {t("home.gallery.lawnRestoration.description")}
                </p>
              </div>
            </div>

            {/* Project 5 - Cut Lawn Pattern */}
            <div
              className="overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <img
                src="/cut-lawn-pattern.jpg"
                alt="Premium lawn pattern services in Montreal by professional landscapers"
                className="w-full h-72 object-cover"
                itemProp="contentUrl"
              />
              <div className="p-4 bg-white">
                <h3
                  className="font-semibold text-lg text-neutral-900"
                  itemProp="name"
                >
                  {t("home.gallery.project3.title")}
                </h3>
                <p className="text-neutral-600 mt-1" itemProp="description">
                  {t("home.gallery.project3.description")}
                </p>
              </div>
            </div>

            {/* Project 6 - Waterfront */}
            <div
              className="overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <img
                src="/water-front-cut-backyard.JPG"
                alt="Waterfront landscaping services in Montreal West Island area"
                className="w-full h-72 object-cover"
                itemProp="contentUrl"
              />
              <div className="p-4 bg-white">
                <h3
                  className="font-semibold text-lg text-neutral-900"
                  itemProp="name"
                >
                  {t("home.gallery.project4.title")}
                </h3>
                <p className="text-neutral-600 mt-1" itemProp="description">
                  {t("home.gallery.project4.description")}
                </p>
              </div>
            </div>

            {/* Project 7 - Trimmed Hedge */}
            <div
              className="overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <img
                src="/trimmed-hedge-cut-lawn.jpg"
                alt="Expert hedge trimming and lawn care services in West Island Montreal"
                className="w-full h-72 object-cover"
                itemProp="contentUrl"
              />
              <div className="p-4 bg-white">
                <h3
                  className="font-semibold text-lg text-neutral-900"
                  itemProp="name"
                >
                  {t("home.gallery.project2.title")}
                </h3>
                <p className="text-neutral-600 mt-1" itemProp="description">
                  {t("home.gallery.project2.description")}
                </p>
              </div>
            </div>

            {/* Project 8 - Equipment */}
            <div
              className="overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <img
                src="/bearz_transport_trailer_on_street.jpeg"
                alt="Bearz Landscaping professional equipment in Montreal"
                className="w-full h-72 object-cover"
                itemProp="contentUrl"
              />
              <div className="p-4 bg-white">
                <h3
                  className="font-semibold text-lg text-neutral-900"
                  itemProp="name"
                >
                  {t("home.gallery.equipment.title")}
                </h3>
                <p className="text-neutral-600 mt-1" itemProp="description">
                  {t("home.gallery.equipment.description")}
                </p>
              </div>
            </div>

            {/* Project 9 - Fresh Cut Lawn */}
            <div
              className="overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <img
                src="/fresh-cut-lawn1.jpg"
                alt="Fresh cut lawn services in Montreal West Island by Bearz Landscaping"
                className="w-full h-72 object-cover"
                itemProp="contentUrl"
              />
              <div className="p-4 bg-white">
                <h3
                  className="font-semibold text-lg text-neutral-900"
                  itemProp="name"
                >
                  {t("home.gallery.project6.title")}
                </h3>
                <p className="text-neutral-600 mt-1" itemProp="description">
                  {t("home.gallery.project6.description")}
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-block bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg px-6 py-3 transition-colors duration-300"
            >
              {t("home.gallery.requestService")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButton />
    </div>
  );
};

export default Home;
